import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { useTheme } from 'contexts/ThemeContext';

import numeral from 'utils/numeral';

import { Text } from 'components/DataDisplay';
import { Container } from 'components/Layout';

const POSTE_COL_WIDTH = 220;
const VERSION_COL_WIDTH = 150;
const NO_VALUE = '—';

const formatAmount = amount => numeral(amount).format('0,0 $');
const formatDelta = delta => numeral(delta).format('+0,0 $');
const formatDate = date =>
  date
    ? new Date(date).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long'
      })
    : null;

const RecapBudgetTable = ({ versions }) => {
  const { themeColors } = useTheme();
  const { designSystem } = themeColors;
  const surfaceBg = themeColors.keep_light;
  const tableRef = useRef(null);

  useEffect(() => {
    const container = tableRef.current?.parentElement;
    if (container) {
      container.scrollLeft = container.scrollWidth;
    }
  }, [versions.length]);

  if (!versions.length) {
    return null;
  }

  const lastIndex = versions.length - 1;
  const tableWidth = POSTE_COL_WIDTH + versions.length * VERSION_COL_WIDTH;
  const referenceLines = versions.find(version => version.lines)?.lines || [];

  const isLastUpsert = (version, index) =>
    index === lastIndex && version.type === 'upsert';

  const getColumnLabel = version =>
    version.type === 'initial'
      ? 'Campagne initiale'
      : `Upsert ${version.index}`;

  const posteSticky = { position: 'sticky', left: 0, zIndex: 2 };

  const versionSticky = index => {
    if (index === 0) {
      return { position: 'sticky', left: POSTE_COL_WIDTH, zIndex: 2 };
    }
    if (index === lastIndex && lastIndex > 0) {
      return { position: 'sticky', right: 0, zIndex: 2 };
    }
    return {};
  };

  const cellBackground = (version, index) => {
    if (version && isLastUpsert(version, index)) {
      return designSystem.violetBg;
    }
    return surfaceBg;
  };

  const baseCell = {
    padding: '9px 12px',
    borderBottom: `1px solid ${designSystem.lineSoft}`,
    whiteSpace: 'nowrap'
  };

  return (
    <Container
      flex
      style={{
        width: '100%',
        boxSizing: 'border-box',
        flexDirection: 'column',
        alignItems: 'stretch',
        border: `1px solid ${designSystem.line}`,
        borderRadius: '10px',
        padding: '14px 16px',
        marginBottom: '12px'
      }}
    >
      <Text
        component="span"
        fontSize="13px"
        fontWeight={700}
        style={{ color: designSystem.inkSoft, marginBottom: '11px' }}
      >
        Récapitulatif budgétaire
      </Text>
      <Container flex style={{ width: '100%', overflowX: 'auto' }}>
        <table
          ref={tableRef}
          style={{
            width: tableWidth,
            tableLayout: 'fixed',
            borderCollapse: 'separate',
            borderSpacing: 0,
            flexShrink: 0
          }}
        >
          <colgroup>
            <col style={{ width: POSTE_COL_WIDTH }} />
            {versions.map(version => (
              <col key={version.key} style={{ width: VERSION_COL_WIDTH }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th
                style={{
                  ...baseCell,
                  ...posteSticky,
                  width: POSTE_COL_WIDTH,
                  minWidth: POSTE_COL_WIDTH,
                  textAlign: 'left',
                  verticalAlign: 'bottom',
                  background: surfaceBg,
                  borderBottom: `1px solid ${designSystem.line}`
                }}
              >
                <Text
                  component="span"
                  fontSize="12.5px"
                  fontWeight={600}
                  style={{ color: designSystem.muted }}
                >
                  Poste
                </Text>
              </th>
              {versions.map((version, index) => {
                const highlighted = isLastUpsert(version, index);
                const requested = formatDate(version.requestedAt);
                const pushed = formatDate(version.pushedAt);
                return (
                  <th
                    key={version.key}
                    style={{
                      ...baseCell,
                      ...versionSticky(index),
                      minWidth: VERSION_COL_WIDTH,
                      textAlign: 'right',
                      verticalAlign: 'top',
                      background: cellBackground(version, index),
                      borderBottom: `1px solid ${designSystem.line}`
                    }}
                  >
                    <Container
                      flex
                      style={{
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: '2px'
                      }}
                    >
                      <Text
                        component="span"
                        fontSize="13px"
                        fontWeight={700}
                        style={{
                          color: highlighted
                            ? designSystem.violet
                            : designSystem.ink
                        }}
                      >
                        {getColumnLabel(version)}
                      </Text>
                      {requested && (
                        <Text
                          component="span"
                          fontSize="11px"
                          fontWeight={400}
                          style={{ color: designSystem.faint }}
                        >
                          {`Demandé le ${requested}`}
                        </Text>
                      )}
                      {version.isPushed ? (
                        pushed && (
                          <Text
                            component="span"
                            fontSize="11px"
                            fontWeight={400}
                            style={{ color: designSystem.faint }}
                          >
                            {`Poussé le ${pushed}`}
                          </Text>
                        )
                      ) : (
                        <Text
                          component="span"
                          fontSize="11px"
                          fontWeight={600}
                          style={{ color: designSystem.violet }}
                        >
                          Non poussé
                        </Text>
                      )}
                    </Container>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {referenceLines.map((referenceLine, rowIndex) => (
              <tr key={referenceLine.label}>
                <td
                  style={{
                    ...baseCell,
                    ...posteSticky,
                    width: POSTE_COL_WIDTH,
                    minWidth: POSTE_COL_WIDTH,
                    textAlign: 'left',
                    background: surfaceBg
                  }}
                >
                  <Text
                    component="span"
                    fontSize="13px"
                    fontWeight={400}
                    style={{ color: designSystem.inkSoft }}
                  >
                    {referenceLine.label}
                  </Text>
                </td>
                {versions.map((version, index) => {
                  const amount = version.lines
                    ? version.lines[rowIndex]?.amount ?? null
                    : null;
                  return (
                    <td
                      key={version.key}
                      style={{
                        ...baseCell,
                        ...versionSticky(index),
                        minWidth: VERSION_COL_WIDTH,
                        textAlign: 'right',
                        background: cellBackground(version, index)
                      }}
                    >
                      <Text
                        component="span"
                        fontSize="13px"
                        fontWeight={400}
                        style={{
                          color:
                            amount === null
                              ? designSystem.faint
                              : designSystem.ink
                        }}
                      >
                        {amount === null ? NO_VALUE : formatAmount(amount)}
                      </Text>
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr>
              <td
                style={{
                  ...baseCell,
                  ...posteSticky,
                  width: POSTE_COL_WIDTH,
                  minWidth: POSTE_COL_WIDTH,
                  textAlign: 'left',
                  borderBottom: 'none',
                  background: surfaceBg
                }}
              >
                <Text
                  component="span"
                  fontSize="13px"
                  fontWeight={700}
                  style={{ color: designSystem.ink }}
                >
                  Total HT
                </Text>
              </td>
              {versions.map((version, index) => {
                const showDelta = version.type === 'upsert' && !!version.delta;
                let deltaColor = designSystem.green;
                if (version.delta < 0) {
                  deltaColor = designSystem.red;
                } else if (!version.isPushed) {
                  deltaColor = designSystem.violet;
                }
                return (
                  <td
                    key={version.key}
                    style={{
                      ...baseCell,
                      ...versionSticky(index),
                      minWidth: VERSION_COL_WIDTH,
                      textAlign: 'right',
                      borderBottom: 'none',
                      background: cellBackground(version, index)
                    }}
                  >
                    <Container
                      flex
                      style={{
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        gap: '2px'
                      }}
                    >
                      <Text
                        component="span"
                        fontSize="13px"
                        fontWeight={700}
                        style={{ color: designSystem.ink }}
                      >
                        {formatAmount(version.totalHT)}
                      </Text>
                      {showDelta && (
                        <Text
                          component="span"
                          fontSize="11.5px"
                          fontWeight={600}
                          style={{ color: deltaColor }}
                        >
                          {formatDelta(version.delta)}
                        </Text>
                      )}
                    </Container>
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </Container>
    </Container>
  );
};

RecapBudgetTable.propTypes = {
  versions: PropTypes.arrayOf(PropTypes.shape()).isRequired
};

export { RecapBudgetTable };
export default RecapBudgetTable;
